import { inject, Injectable } from '@angular/core'
import { firstValueFrom, forkJoin, Observable } from 'rxjs'
import { FeathersService } from '../../feathers.service'
import { concatMap, defaultIfEmpty, first, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { Assistant } from '../../../models/assistant'
import { DateRange } from '../../date-utils.service'
import { Assistance } from '../../../models/assistance'
import { AlertController, ModalController, NavController } from '@ionic/angular'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { AssistanceStatusesService } from '../codelists/assistance-statuses.service'
import {
  SimpleAssistanceListComponent,
} from '../../../features/assistance/simple-assistance-list/simple-assistance-list.component'
import { WorkerService } from '../worker/worker.service'

@Injectable({
  providedIn: 'root',
})
export class AssistantService {
  private feathers = inject(FeathersService)
  private statusService = inject(AssistanceStatusesService)
  private modalCtrl = inject(ModalController)

  allWithoutAssignments$: Observable<Assistant[]>
  activeFullyPopulated$: Observable<Assistant[]>
  activeWithUser$: Observable<Assistant[]>
  activeWithUserAndKeyCoordinator$: Observable<Assistant[]>
  private TAG: string = 'AssistantService'

  private init() {
    this.allWithoutAssignments$ = this.feathers
      .find(Assistant, {})
      .pipe(tap((r) => console.log(this.TAG, 'all assistants loaded', r)))
      .pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getAllCachedWithoutAssignments(): Observable<Assistant[]> {
    return this.feathers
      .find(Assistant, {
        queryType: 'fullWithoutAssignments',
      })
      .pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  public getAssistantWithoutAssignmentsByUserId(userId: number): Observable<Assistant> {
    return this.feathers
      .find(Assistant, {
        queryType: 'fullWithoutAssignments',
        userId,
        __skipAllocationJoinMerge: true, // Skip case: role assistant - assistances dashboard
      })
      .pipe(
        map((a) => {
          if (!a[0]) throw new Error(`No assistant for user ${userId}`)
          return a[0]
        }),
      )
  }

  getAssistantIdByUserId(userId: number): Observable<number> {
    return this.feathers
      .find(Assistant, {
        queryType: 'withUser',
        $select: ['id'],
        userId,
      })
      .pipe(map((a) => a[0]?.id))
  }

  public getAssistantWithoutAssignmentsById(id: number): Observable<Assistant> {
    return this.feathers
      .find(Assistant, {
        queryType: 'fullWithoutAssignments',
        id,
      })
      .pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        map((c) => c[0]),
      )
  }

  public getAssistantWithoutAssignmentsByWorkerId(workerId: number): Observable<Assistant> {
    return this.feathers
      .find(Assistant, {
        queryType: 'fullWithoutAssignments',
        workerId,
      })
      .pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        map((c) => c[0]),
      )
  }

  getAllActiveFullyPopulatedWithCaching() {
    if (!this.activeFullyPopulated$) this.initActiveFullyPopulated()
    return this.activeFullyPopulated$
  }

  initActiveFullyPopulated() {
    this.activeFullyPopulated$ = this.feathers
      .find(Assistant, { excludeDeactivated: true })
      .pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getAllActiveWithUserWithCaching() {
    if (!this.activeWithUser$) this.initActiveWithUser()
    return this.activeWithUser$
  }

  initActiveWithUser() {
    this.activeWithUser$ = this.feathers
      .find(Assistant, {
        queryType: 'withUser',
        excludeDeactivated: true,
      })
      .pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getAllActiveWithUserAndKeyCoordinatorWithCaching() {
    if (!this.activeWithUserAndKeyCoordinator$) this.initActiveWithUserAndKeyCoordinator()
    return this.activeWithUserAndKeyCoordinator$
  }

  initActiveWithUserAndKeyCoordinator() {
    this.activeWithUserAndKeyCoordinator$ = this.feathers
      .find(Assistant, {
        queryType: 'withUserAndKeyCoordinator',
        excludeDeactivated: true,
      })
      .pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getAllActiveAssistantsWithAssistanceInRange(range: DateRange): Observable<Assistant[]> {
    return this.feathers.find(Assistant, {
      queryType: 'hasPositiveAssistanceInRange',
      excludeDeactivated: true,
      range,
    })
  }

  getAllUnbilledAssistances(assistantId: number, limit?: number): Observable<Assistance[]> {
    const allowedStatusNames = [
      AssistanceStatusName.PLANNED,
      AssistanceStatusName.HAS_ASSISTANT,
      AssistanceStatusName.SENT_TO_ASSISTANT,
      AssistanceStatusName.CONFIRMED,
      AssistanceStatusName.CANCELED_LATE,
    ]

    return this.statusService.getByNames(allowedStatusNames).pipe(
      switchMap((statuses) => {
        return this.feathers.find(Assistance, {
          queryType: 'narrowToAssistant',
          $params_assistantId: assistantId,
          $limit: limit,
          $sort: { plannedFrom: 1 },
          isBilled: false,
          statusId: { $in: statuses.map((s) => s.id) },
        })
      }),
      concatMap((assistances: Assistance[]) => {
        const observables = assistances.map((a) => a.addStatusFromId(this.statusService))
        return forkJoin(observables).pipe(defaultIfEmpty([]))
      }),
      map((assistances: Assistance[]) =>
        assistances.filter((a) => {
          return (
            allowedStatusNames.includes(a.status.name) &&
            a.assistantAssignments.hasAssistant(assistantId) &&
            !a.isBilled
          )
        }),
      ),
    )
  }

  hasAnyUnbilledAssistance(assistantId: number): Observable<boolean> {
    return this.getAllUnbilledAssistances(assistantId, 1).pipe(
      map((assistances: Assistance[]) => assistances.length > 0),
      first(),
    )
  }

  async createCannotDeactivateModal(unbilledAssistances$: Observable<Assistance[]>, fullName: string) {
    const modal = await this.modalCtrl.create({
      component: SimpleAssistanceListComponent,
      cssClass: 'simpleAssistanceList',
      componentProps: {
        assistances$: unbilledAssistances$,
        headerText: `Nelze deaktivovat - ${fullName}`,
        instructionText:
          'Asistent nemá uzavřené všechny asistence. V seznamu níže vidíte asistence, které brání deaktivaci asistenta. Pokud chcete asistenta deaktivovat, tyto asistence buď uzavřete, zrušte anebo smažte.',
      },
    })

    return await modal.present()
  }

  async createInfoPastDeactivationAlert(
    alertCtrl: AlertController,
    navCtrl: NavController,
    assistantFullname: string,
    assistantId: number,
  ) {
    const alert = await alertCtrl.create({
      header: 'Nezapomeňte upravit platnost smluv asistenta',
      message: 'Opravdu máte upravené platnosti smluv asistenta <br><strong>' + assistantFullname + '</strong>?',
      inputs: [],
      buttons: [
        {
          text: 'Ano',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Ne, přejít na smlouvy',
          cssClass: 'primary',
          handler: () => {
            navCtrl.navigateRoot(`/menu/app/tabs/workers/assistant/${assistantId}/work-info`)
          },
        },
      ],
    })

    await alert.present().then(() => {
      const firstInput: any = document.querySelector('.alert-button.primary')
      firstInput.focus()
      return
    })
  }

  // TODO task 389
  async createDeletionConfirmationAlertForUser(alertCtrl: AlertController) {
    return new Promise(async (resolve) => {
      const alert = await alertCtrl.create({
        header: 'Opravdu smazat?',
        message: 'Opravdu si přejete smazat asistenta?',
        buttons: [
          {
            text: 'Zrušit',
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              resolve(false)
            },
          },
          {
            text: 'Ano, smazat',
            cssClass: 'primary',
            handler: () => {
              resolve(true)
            },
          },
        ],
      })
      alert.present()
    })
  }

  async createDeactivationConfirmationAlert(assistant: Assistant, alertCtrl: AlertController, navCtrl: NavController) {
    const alert = await alertCtrl.create({
      header: 'Opravdu deaktivovat?',
      message:
        'Opravdu si přejete deaktivovat asistenta<br><strong>' + assistant.worker.user.fullNameReverse() + '</strong>?',
      inputs: [
        {
          name: 'deactivationReason',
          type: 'text',
          placeholder: 'Důvod deaktivace (povinný)',
          label: 'Důvod deaktivace',
          attributes: {
            required: true,
          },
          value: assistant.worker.deactivationReason,
        },
      ],
      buttons: [
        {
          text: 'Zrušit',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Ano, deaktivovat',
          cssClass: 'primary',
          handler: (info) => {
            console.log(info)
            if (info.deactivationReason.length > 0) {
              this.deactivate(assistant, info.deactivationReason)
              this.createInfoPastDeactivationAlert(alertCtrl, navCtrl, assistant.worker.user.fullName(), assistant.id)
            } else {
              this.createDeactivationConfirmationAlert(assistant, alertCtrl, navCtrl)
            }
          },
        },
      ],
    })

    await alert.present().then(() => {
      const firstInput: any = document.querySelector('.alert-button.primary')
      firstInput.focus()
      return
    })
  }

  async deactivateWithConfirmation(assistant: Assistant, alertCtrl: AlertController, navCtrl: NavController) {
    const unbilledAssistances$ = this.getAllUnbilledAssistances(assistant.id)
    const hasAnyUnbilledAssistances = await firstValueFrom(this.hasAnyUnbilledAssistance(assistant.id))

    if (hasAnyUnbilledAssistances) {
      await this.createCannotDeactivateModal(unbilledAssistances$, assistant.worker.user.fullName())
    } else {
      await this.createDeactivationConfirmationAlert(assistant, alertCtrl, navCtrl)
    }
  }

  update(assistant: Assistant) {
    return this.feathers.update<Assistant>('assistants', assistant)
  }

  deactivate(a: Assistant, reason: string) {
    const wa = WorkerService.getPrimaryWorkerAllocation(a.worker)
    wa.deactivatedAt = new Date()
    wa.isDeactivated = true
    wa.deactivationReason = reason
    return this.update(a)
  }

  activate(a: Assistant) {
    const wa = WorkerService.getPrimaryWorkerAllocation(a.worker)
    wa.deactivatedAt = null
    wa.isDeactivated = false
    return this.update(a)
  }
}
